import {getCurrentUser} from './supabase'

// API helper for support messages operations - NO getServiceRoleClient() in browser
async function callSupportMessagesAPI(operation: string, data: Record<string, any> = {}): Promise<any> {
 const response = await fetch('/api/user/support-messages', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ operation, data })
 });
 return response.json();
}

export interface SupportMessage {
 id: string
 sender_id: number
 recipient_id: number
 subject: string
 message: string
 status?: string
 created_at: string
}

let cachedAdminUserId: number | null = null

// Resolve the default admin recipient: prefer AUREUS user, else first admin_users email that matches users.email
export const getDefaultAdminRecipientId = async (): Promise<number | null> => {
 if (cachedAdminUserId) return cachedAdminUserId
 try {
 // Use API endpoint - NO getServiceRoleClient() in browser
 const result = await callSupportMessagesAPI('getDefaultAdminRecipientId', {})

 if (result.success && result.adminId) {
 cachedAdminUserId = result.adminId
 return result.adminId
 }
 } catch (e) {
 console.error('getDefaultAdminRecipientId failed:', e)
 }
 return null
}

export const getCurrentDbUserId = async (): Promise<number | null> => {
 try {
 const u = await getCurrentUser()
 const rawId = u?.database_user?.id || u?.user_metadata?.user_id

 if (rawId === null || rawId === undefined) {
 return null
 }

 // Handle numeric ID
 if (typeof rawId === 'number') {
 return rawId
 }

 // Handle string ID - remove "db_" prefix if present and parse
 if (typeof rawId === 'string') {
 const cleanedId = rawId.replace(/db_/g, '').trim()
 const numericId = parseInt(cleanedId, 10)
 if (!isNaN(numericId)) {
 return numericId
 }
 }

 return null
 } catch {
 return null
 }
}

export const fetchConversation = async (userId: number, adminId: number): Promise<SupportMessage[]> => {
 try {
 // Use API endpoint - NO getServiceRoleClient() in browser
 const result = await callSupportMessagesAPI('fetchConversation', { userId, adminId })

 if (!result.success) {
 console.error('fetchConversation error:', result.error)
 return []
 }
 return (result.messages || []) as SupportMessage[]
 } catch (error) {
 console.error('fetchConversation error:', error)
 return []
 }
}

export const sendSupportMessageAsUser = async (userId: number, adminId: number, message: string, subject = 'Support') => {
 try {
 // Use API endpoint directly - NO getServiceRoleClient() fallbacks in browser
 const result = await callSupportMessagesAPI('sendSupportMessage', {
 senderId: userId,
 recipientId: adminId,
 subject,
 message
 })

 if (!result.success) return { success: false, error: result.error }
 return { success: true }
 } catch (error: any) {
 console.error('sendSupportMessageAsUser error:', error)
 return { success: false, error: error.message || 'Failed to send message' }
 }
}

export const sendSupportMessageAsAdmin = async (adminUserId: number, userId: number, message: string, subject = 'Support Reply') => {
 try {
 // Use API endpoint - NO getServiceRoleClient() in browser
 const result = await callSupportMessagesAPI('sendSupportMessage', {
 senderId: adminUserId,
 recipientId: userId,
 subject,
 message
 })

 if (!result.success) return { success: false, error: result.error }
 return { success: true }
 } catch (error: any) {
 console.error('sendSupportMessageAsAdmin error:', error)
 return { success: false, error: error.message || 'Failed to send message' }
 }
}

// Admin functions for Quick Chat management
export interface QuickChatConversation {
 user_id: number
 username: string
 first_name: string | null
 last_name: string | null
 email: string | null
 last_message: string
 last_message_at: string
 unread_count: number
}

export const getQuickChatConversations = async (adminUserId: number): Promise<QuickChatConversation[]> => {
 try {
 // Use API endpoint - NO getServiceRoleClient() in browser
 const result = await callSupportMessagesAPI('getQuickChatConversations', { adminUserId })

 if (!result.success) {
 console.error('getQuickChatConversations error:', result.error)
 return []
 }

 // API already handles all the grouping and user detail enrichment
 return result.conversations || []
 } catch (e) {
 console.error('getQuickChatConversations error:', e)
 return []
 }
}

export const getQuickChatMessagesForUser = async (adminUserId: number, userId: number): Promise<SupportMessage[]> => {
 try {
 // Use API endpoint - NO getServiceRoleClient() in browser
 const result = await callSupportMessagesAPI('getQuickChatMessagesForUser', { adminUserId, userId })

 if (!result.success) {
 console.error('getQuickChatMessagesForUser error:', result.error)
 return []
 }
 return (result.messages || []) as SupportMessage[]
 } catch (error) {
 console.error('getQuickChatMessagesForUser error:', error)
 return []
 }
}


export const markQuickChatRead = async (adminUserId: number, userId: number): Promise<boolean> => {
 try {
 const result = await callSupportMessagesAPI('markQuickChatRead', { adminUserId, userId })
 return result.success === true
 } catch (e) {
 console.error('markQuickChatRead error:', e)
 return false
 }
}
